import { useEventBus } from './chunk-CHNC4UYE.js';
import { ar_default, sl_default } from './chunk-CB64IOYD.js';
import { DEFAULT_CURRENCY } from './chunk-6ZCU3BQN.js';
import { en_default } from './chunk-FYX64CDL.js';
import React, { createContext, useCallback, useSyncExternalStore, useRef, useState, useContext, useEffect, useMemo } from 'react';
import { mergeEntityFrame, resolvePageMeta } from '@almadar/core';
import { createLogger } from '@almadar/logger';

function useMediaQuery(query) {
  const subscribe = useCallback(
    (onChange) => {
      if (!hasMatchMedia()) return () => void 0;
      const mql = window.matchMedia(query);
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    },
    [query]
  );
  return useSyncExternalStore(
    subscribe,
    () => hasMatchMedia() && window.matchMedia(query).matches,
    () => false
  );
}
function hasMatchMedia() {
  return typeof window !== "undefined" && typeof window.matchMedia === "function";
}
var counts = /* @__PURE__ */ new Map();
var keyListeners = /* @__PURE__ */ new Map();
var seq = 0;
function notify(key) {
  keyListeners.get(key)?.forEach((cb) => cb());
}
function newPendingKey() {
  seq += 1;
  return `pending-${seq}`;
}
function beginPending(key) {
  counts.set(key, (counts.get(key) ?? 0) + 1);
  if (counts.get(key) === 1) notify(key);
}
function endPending(key) {
  const n = (counts.get(key) ?? 0) - 1;
  if (n > 0) {
    counts.set(key, n);
    return;
  }
  counts.delete(key);
  notify(key);
}
function isPending(key) {
  return (counts.get(key) ?? 0) > 0;
}
function subscribePending(key, cb) {
  let set = keyListeners.get(key);
  if (!set) {
    set = /* @__PURE__ */ new Set();
    keyListeners.set(key, set);
  }
  set.add(cb);
  return () => {
    set?.delete(cb);
    if (set && set.size === 0) keyListeners.delete(key);
  };
}
var PendingScopeContext = createContext(null);
function usePendingScopeValue() {
  const keys = useRef(/* @__PURE__ */ new Set());
  const listeners = useRef(/* @__PURE__ */ new Set());
  const emitChange = useCallback(() => listeners.current.forEach((cb) => cb()), []);
  return useState(() => ({
    track: (key) => {
      if (!isPending(key)) return;
      keys.current.add(key);
      emitChange();
      const unsub = subscribePending(key, () => {
        if (isPending(key)) return;
        keys.current.delete(key);
        unsub();
        emitChange();
      });
    },
    hasPending: () => keys.current.size > 0,
    subscribe: (cb) => {
      listeners.current.add(cb);
      return () => listeners.current.delete(cb);
    }
  }))[0];
}
function useScopeHasPending(scope) {
  return useSyncExternalStore(
    useCallback((cb) => scope ? scope.subscribe(cb) : () => {
    }, [scope]),
    () => scope ? scope.hasPending() : false,
    () => false
  );
}
function usePendingAction() {
  const scope = useContext(PendingScopeContext);
  const [key, setKey] = useState(null);
  const pending = useSyncExternalStore(
    useCallback((cb) => key ? subscribePending(key, cb) : () => {
    }, [key]),
    () => key ? isPending(key) : false,
    () => false
  );
  useEffect(() => {
    if (key && !pending) setKey(null);
  }, [key, pending]);
  const activate = useCallback((emit) => {
    const k = newPendingKey();
    emit(k);
    if (!isPending(k)) return;
    scope?.track(k);
    setKey(k);
  }, [scope]);
  return { pending, activate };
}
function usePendingRows() {
  const [rows, setRows] = useState(/* @__PURE__ */ new Map());
  const unsubs = useRef(/* @__PURE__ */ new Map());
  useEffect(() => () => unsubs.current.forEach((u) => u()), []);
  const activateRow = useCallback((rowId, emit) => {
    const k = newPendingKey();
    emit(k);
    if (!isPending(k)) return;
    setRows((prev) => new Map(prev).set(rowId, k));
    unsubs.current.get(rowId)?.();
    unsubs.current.set(rowId, subscribePending(k, () => {
      if (isPending(k)) return;
      unsubs.current.get(rowId)?.();
      unsubs.current.delete(rowId);
      setRows((prev) => {
        if (prev.get(rowId) !== k) return prev;
        const next = new Map(prev);
        next.delete(rowId);
        return next;
      });
    }));
  }, []);
  const isRowPending = useCallback((rowId) => rows.has(rowId), [rows]);
  return { isRowPending, activateRow };
}
function useInfiniteScroll(onLoadMore, options = {}) {
  const { rootMargin = "200px", hasMore = true, isLoading = false } = options;
  const observerRef = useRef(null);
  const callbackRef = useRef(onLoadMore);
  callbackRef.current = onLoadMore;
  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;
  const isLoadingRef = useRef(isLoading);
  isLoadingRef.current = isLoading;
  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
    };
  }, []);
  const sentinelRef = useCallback((node) => {
    observerRef.current?.disconnect();
    if (!node) return;
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting && hasMoreRef.current && !isLoadingRef.current) {
          callbackRef.current();
        }
      },
      { rootMargin }
    );
    observerRef.current.observe(node);
  }, [rootMargin]);
  return { sentinelRef };
}

// hooks/usePrefersReducedMotion.ts
function usePrefersReducedMotion() {
  return useMediaQuery("(prefers-reduced-motion: reduce)");
}
function useSwipeGesture(callbacks, options = {}) {
  const { threshold = 50, velocityThreshold = 0.3, preventDefault = false } = options;
  const startX = useRef(0);
  const startY = useRef(0);
  const startTime = useRef(0);
  const currentX = useRef(0);
  const tracking = useRef(false);
  const offsetXRef = useRef(0);
  const isSwipingRef = useRef(false);
  const onPointerDown = useCallback((e) => {
    startX.current = e.clientX;
    startY.current = e.clientY;
    currentX.current = e.clientX;
    startTime.current = Date.now();
    tracking.current = true;
    isSwipingRef.current = false;
    offsetXRef.current = 0;
    e.target.setPointerCapture?.(e.pointerId);
  }, []);
  const onPointerMove = useCallback((e) => {
    if (!tracking.current) return;
    if (preventDefault) e.preventDefault();
    currentX.current = e.clientX;
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;
    if (Math.abs(dx) > 10 && Math.abs(dx) > Math.abs(dy)) {
      isSwipingRef.current = true;
      offsetXRef.current = dx;
    }
  }, [preventDefault]);
  const onPointerUp = useCallback((e) => {
    if (!tracking.current) return;
    tracking.current = false;
    const dx = e.clientX - startX.current;
    const dy = e.clientY - startY.current;
    const elapsed = Date.now() - startTime.current;
    const velocity = Math.abs(dx) / Math.max(elapsed, 1);
    offsetXRef.current = 0;
    isSwipingRef.current = false;
    if (Math.abs(dx) < threshold && velocity < velocityThreshold) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx < -threshold) callbacks.onSwipeLeft?.();
      else if (dx > threshold) callbacks.onSwipeRight?.();
    } else {
      if (dy < -threshold) callbacks.onSwipeUp?.();
      else if (dy > threshold) callbacks.onSwipeDown?.();
    }
  }, [threshold, velocityThreshold, callbacks]);
  const onPointerCancel = useCallback(() => {
    tracking.current = false;
    offsetXRef.current = 0;
    isSwipingRef.current = false;
  }, []);
  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    offsetX: offsetXRef.current,
    isSwiping: isSwipingRef.current
  };
}
function localPoint(canvas, clientX, clientY) {
  if (!canvas) return { x: clientX, y: clientY };
  const rect = canvas.getBoundingClientRect();
  return { x: clientX - rect.left, y: clientY - rect.top };
}
function useCanvasGestures(options) {
  const {
    canvasRef,
    enabled = true,
    wheelStep = 1.1,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onZoom,
    onPanDelta,
    onMultiTouchStart
  } = options;
  const pointers = useRef(/* @__PURE__ */ new Map());
  const pinch = useRef(null);
  const computePinch = useCallback(() => {
    const pts = [...pointers.current.values()];
    const dx = pts[1].x - pts[0].x;
    const dy = pts[1].y - pts[0].y;
    return {
      distance: Math.hypot(dx, dy) || 1,
      centroid: { x: (pts[0].x + pts[1].x) / 2, y: (pts[0].y + pts[1].y) / 2 }
    };
  }, []);
  const handlePointerDown = useCallback(
    (e) => {
      if (!enabled) return;
      e.currentTarget.setPointerCapture(e.pointerId);
      pointers.current.set(e.pointerId, localPoint(canvasRef.current, e.clientX, e.clientY));
      if (pointers.current.size === 2) {
        onMultiTouchStart?.();
        pinch.current = computePinch();
      } else if (pointers.current.size === 1) {
        onPointerDown?.(e);
      }
    },
    [enabled, canvasRef, onMultiTouchStart, computePinch, onPointerDown]
  );
  const handlePointerMove = useCallback(
    (e) => {
      if (!enabled) return;
      if (pointers.current.has(e.pointerId)) {
        pointers.current.set(e.pointerId, localPoint(canvasRef.current, e.clientX, e.clientY));
      }
      if (pointers.current.size >= 2 && pinch.current) {
        const next = computePinch();
        const prev = pinch.current;
        if (next.distance !== prev.distance) {
          onZoom?.(next.distance / prev.distance, next.centroid.x, next.centroid.y);
        }
        onPanDelta?.(next.centroid.x - prev.centroid.x, next.centroid.y - prev.centroid.y);
        pinch.current = next;
        return;
      }
      onPointerMove?.(e);
    },
    [enabled, canvasRef, computePinch, onZoom, onPanDelta, onPointerMove]
  );
  const endPointer = useCallback(
    (e, fireUp) => {
      const wasMulti = pointers.current.size >= 2;
      pointers.current.delete(e.pointerId);
      if (pointers.current.size < 2) pinch.current = null;
      if (wasMulti && pointers.current.size === 1) return;
      if (pointers.current.size === 0 && fireUp) onPointerUp?.(e);
    },
    [onPointerUp]
  );
  const handlePointerUp = useCallback(
    (e) => {
      if (!enabled) return;
      endPointer(e, true);
    },
    [enabled, endPointer]
  );
  const handlePointerCancel = useCallback(
    (e) => {
      if (!enabled) return;
      endPointer(e, false);
    },
    [enabled, endPointer]
  );
  const handleWheel = useCallback(
    (e) => {
      if (!enabled) return;
      e.preventDefault();
      const { x, y } = localPoint(canvasRef.current, e.clientX, e.clientY);
      onZoom?.(e.deltaY < 0 ? wheelStep : 1 / wheelStep, x, y);
    },
    [enabled, canvasRef, wheelStep, onZoom]
  );
  return {
    onPointerDown: handlePointerDown,
    onPointerMove: handlePointerMove,
    onPointerUp: handlePointerUp,
    onPointerCancel: handlePointerCancel,
    onWheel: handleWheel
  };
}
function useLongPress(onLongPress, options = {}) {
  const { duration = 500, moveThreshold = 10 } = options;
  const timerRef = useRef(null);
  const startPos = useRef({ x: 0, y: 0 });
  const isPressedRef = useRef(false);
  const firedRef = useRef(false);
  const cancel = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    isPressedRef.current = false;
  }, []);
  const onPointerDown = useCallback((e) => {
    firedRef.current = false;
    startPos.current = { x: e.clientX, y: e.clientY };
    isPressedRef.current = true;
    timerRef.current = setTimeout(() => {
      firedRef.current = true;
      isPressedRef.current = false;
      onLongPress();
    }, duration);
  }, [duration, onLongPress]);
  const onPointerMove = useCallback((e) => {
    if (!isPressedRef.current) return;
    const dx = e.clientX - startPos.current.x;
    const dy = e.clientY - startPos.current.y;
    if (Math.sqrt(dx * dx + dy * dy) > moveThreshold) {
      cancel();
    }
  }, [moveThreshold, cancel]);
  const onPointerUp = useCallback(() => {
    cancel();
  }, [cancel]);
  const onPointerCancel = useCallback(() => {
    cancel();
  }, [cancel]);
  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    isPressed: isPressedRef.current
  };
}

// hooks/useAuthContext.ts
function useAuthContext() {
  return {
    user: null,
    loading: false,
    signIn: void 0,
    signOut: void 0
  };
}
var ALMADAR_DND_MIME = "application/x-almadar-dnd";
function useDraggable({ payload, disabled = false }) {
  const [isDragging, setIsDragging] = useState(false);
  const eventBus = useEventBus();
  const handleDragStart = useCallback(
    (e) => {
      if (disabled) {
        e.preventDefault();
        return;
      }
      e.dataTransfer.setData(ALMADAR_DND_MIME, JSON.stringify(payload));
      e.dataTransfer.effectAllowed = "copy";
      setIsDragging(true);
      eventBus.emit("UI:DRAG_START", { kind: payload.kind, data: payload.data });
    },
    [disabled, payload, eventBus]
  );
  const handleDragEnd = useCallback(
    (e) => {
      setIsDragging(false);
      eventBus.emit("UI:DRAG_END", { kind: payload.kind, data: payload.data });
    },
    [payload, eventBus]
  );
  const dragProps = useMemo(
    () => ({
      draggable: !disabled,
      onDragStart: handleDragStart,
      onDragEnd: handleDragEnd,
      "aria-grabbed": isDragging
    }),
    [disabled, handleDragStart, handleDragEnd, isDragging]
  );
  return { dragProps, isDragging };
}
function parsePayload(e) {
  try {
    const raw = e.dataTransfer.getData(ALMADAR_DND_MIME);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed.kind !== "string" || !parsed.data) return null;
    return parsed;
  } catch {
    return null;
  }
}
function hasAlmadarPayload(e) {
  return e.dataTransfer.types.includes(ALMADAR_DND_MIME);
}
function useDropZone({ accepts, onDrop, disabled = false }) {
  const [isOver, setIsOver] = useState(false);
  const eventBus = useEventBus();
  const handleDragOver = useCallback(
    (e) => {
      if (disabled) return;
      if (!hasAlmadarPayload(e)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "copy";
      setIsOver(true);
    },
    [disabled]
  );
  const handleDragLeave = useCallback(
    (e) => {
      setIsOver(false);
    },
    []
  );
  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsOver(false);
      if (disabled) return;
      const payload = parsePayload(e);
      if (!payload) return;
      if (!accepts.includes(payload.kind)) return;
      const position = { x: e.clientX, y: e.clientY };
      onDrop(payload, position);
      eventBus.emit("UI:DROP", { kind: payload.kind, data: payload.data, ...position });
    },
    [disabled, accepts, onDrop, eventBus]
  );
  const dropProps = useMemo(
    () => ({
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop
    }),
    [handleDragOver, handleDragLeave, handleDrop]
  );
  return { dropProps, isOver };
}
var queryStores = /* @__PURE__ */ new Map();
function getOrCreateStore(query) {
  if (!queryStores.has(query)) {
    queryStores.set(query, {
      search: "",
      filters: {},
      sortField: void 0,
      sortDirection: void 0,
      listeners: /* @__PURE__ */ new Set()
    });
  }
  return queryStores.get(query);
}
function useQuerySingleton(query) {
  const [, forceUpdate] = useState({});
  if (!query) {
    return null;
  }
  const store = useMemo(() => getOrCreateStore(query), [query]);
  useMemo(() => {
    const listener = () => forceUpdate({});
    store.listeners.add(listener);
    return () => {
      store.listeners.delete(listener);
    };
  }, [store]);
  const notifyListeners = useCallback(() => {
    store.listeners.forEach((listener) => listener());
  }, [store]);
  const setSearch = useCallback((value) => {
    store.search = value;
    notifyListeners();
  }, [store, notifyListeners]);
  const setFilter = useCallback((key, value) => {
    store.filters = { ...store.filters, [key]: value };
    notifyListeners();
  }, [store, notifyListeners]);
  const clearFilters = useCallback(() => {
    store.filters = {};
    store.search = "";
    notifyListeners();
  }, [store, notifyListeners]);
  const setSort = useCallback((field, direction) => {
    store.sortField = field;
    store.sortDirection = direction;
    notifyListeners();
  }, [store, notifyListeners]);
  return {
    search: store.search,
    setSearch,
    filters: store.filters,
    setFilter,
    clearFilters,
    sortField: store.sortField,
    sortDirection: store.sortDirection,
    setSort
  };
}
function parseQueryBinding(binding) {
  const cleaned = binding.startsWith("@") ? binding.slice(1) : binding;
  const parts = cleaned.split(".");
  return {
    query: parts[0],
    field: parts.length > 1 ? parts.slice(1).join(".") : void 0
  };
}
var { $meta: _meta, ...coreMessages } = en_default;
var coreLocale = coreMessages;
var { $meta: _arMeta, ...arMessages } = ar_default;
var { $meta: _slMeta, ...slMessages } = sl_default;
var uiLocales = { en: coreLocale, ar: arMessages, sl: slMessages };
var I18nContext = createContext({
  locale: "en",
  direction: "ltr",
  t: (key, params) => {
    let msg = coreLocale[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        msg = msg.split(`{{${k}}}`).join(String(v));
      }
    }
    return msg;
  }
});
I18nContext.displayName = "I18nContext";
function I18nProvider({ value, children, documentRoot }) {
  useEffect(() => {
    if (!documentRoot) return;
    document.documentElement.lang = value.locale;
    document.documentElement.dir = value.direction;
  }, [documentRoot, value.locale, value.direction]);
  return React.createElement(I18nContext.Provider, { value }, children);
}
function useTranslate() {
  return useContext(I18nContext);
}
function useRenderI18n() {
  const { locale, messages } = useContext(I18nContext);
  return useMemo(() => messages !== void 0 ? { locale, messages } : void 0, [locale, messages]);
}
function useFormatContext() {
  const { t, locale, currency } = useContext(I18nContext);
  return useMemo(
    () => ({ locale, currency: currency ?? DEFAULT_CURRENCY, booleanLabels: { yes: t("common.yes"), no: t("common.no") } }),
    [t, locale, currency]
  );
}
function createTranslate(messages, locale) {
  const uiCatalog = locale !== void 0 ? uiLocales[locale] : void 0;
  return (key, params) => {
    let msg = messages[key] ?? uiCatalog?.[key] ?? coreLocale[key] ?? key;
    if (params) {
      for (const [k, v] of Object.entries(params)) {
        msg = msg.split(`{{${k}}}`).join(String(v));
      }
    }
    return msg;
  };
}

// lib/bus-claim.ts
var claims = /* @__PURE__ */ new WeakMap();
function claimDelivery(source, trait, event) {
  if (source === void 0) return true;
  const key = `${trait}\0${event}`;
  const seen = claims.get(source);
  if (seen === void 0) {
    claims.set(source, /* @__PURE__ */ new Set([key]));
    return true;
  }
  if (seen.has(key)) return false;
  seen.add(key);
  return true;
}
var log = createLogger("almadar:ui:shared-entity-store");
var EMPTY_ENTITY_STATE = {};
function createSharedEntityStore() {
  const states = /* @__PURE__ */ new Map();
  const subscribers = /* @__PURE__ */ new Map();
  const getSnapshot = (entityId) => states.get(entityId) ?? EMPTY_ENTITY_STATE;
  const subscribe = (entityId, callback) => {
    let set = subscribers.get(entityId);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      subscribers.set(entityId, set);
    }
    set.add(callback);
    return () => {
      const current = subscribers.get(entityId);
      if (!current) return;
      current.delete(callback);
      if (current.size === 0) {
        subscribers.delete(entityId);
      }
    };
  };
  const commit = (entityId, nextState) => {
    states.set(entityId, nextState);
    const set = subscribers.get(entityId);
    if (!set) return;
    set.forEach((callback) => {
      try {
        callback();
      } catch (error) {
        log.error("Shared entity subscriber error", {
          entityId,
          error: error instanceof Error ? error : String(error)
        });
      }
    });
  };
  const seed = (entityId, initialState) => {
    if (!states.has(entityId)) {
      states.set(entityId, initialState);
    }
  };
  return { getSnapshot, subscribe, commit, seed };
}
function useSharedEntityStore() {
  const storeRef = useRef(null);
  if (storeRef.current === null) {
    storeRef.current = createSharedEntityStore();
  }
  return storeRef.current;
}
var SharedEntityStoreContext = createContext(null);
function useSharedEntityStoreContext() {
  const store = useContext(SharedEntityStoreContext);
  if (!store) {
    throw new Error(
      "useSharedEntityStoreContext: no SharedEntityStoreContext.Provider found in the component tree"
    );
  }
  return store;
}
function useSharedEntitySnapshot(store, entityId) {
  return useSyncExternalStore(
    (onStoreChange) => store.subscribe(entityId, onStoreChange),
    () => store.getSnapshot(entityId)
  );
}
function runTickFrame(entityId, orderedWriters, store) {
  let scratch = store.getSnapshot(entityId);
  for (const writer of orderedWriters) {
    const writes = writer(scratch);
    scratch = mergeEntityFrame(scratch, writes);
  }
  store.commit(entityId, scratch);
  return scratch;
}
var OWNED = "data-orb-head";
var shellTitle;
function ownedTag(tag, attrs) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, v);
  el.setAttribute(OWNED, "");
  return el;
}
function usePageHead(input) {
  const { messages, locale } = useTranslate();
  const title = input ? resolvePageMeta(input.title, (k) => messages?.[k]) : void 0;
  const description = input ? resolvePageMeta(input.description, (k) => messages?.[k]) : void 0;
  const isPublic = input?.access !== "authenticated" && input?.indexing !== "noindex";
  const canonical = input?.origin !== void 0 ? `${input.origin.replace(/\/+$/, "")}${input.path}` : void 0;
  const siteName = input?.siteName;
  const active = input !== null;
  const originBase = input?.origin?.replace(/\/+$/, "");
  const hreflang = [];
  if (originBase !== void 0) {
    for (const alt of input?.alternates ?? []) hreflang.push([alt.locale, `${originBase}${alt.path}`]);
    const fallback = input?.alternates?.find((alt) => alt.locale === input.defaultLocale);
    if (fallback !== void 0) hreflang.push(["x-default", `${originBase}${fallback.path}`]);
  }
  const hreflangKey = hreflang.map(([lang, href]) => `${lang} ${href}`).join("\n");
  useEffect(() => {
    if (!active || typeof document === "undefined") return void 0;
    shellTitle ?? (shellTitle = document.title);
    document.title = title ?? siteName ?? shellTitle;
    const tags = [];
    if (!isPublic) tags.push(ownedTag("meta", { name: "robots", content: "noindex" }));
    if (isPublic && description !== void 0) {
      tags.push(ownedTag("meta", { name: "description", content: description }));
      tags.push(ownedTag("meta", { property: "og:description", content: description }));
    }
    if (isPublic && title !== void 0) tags.push(ownedTag("meta", { property: "og:title", content: title }));
    if (isPublic && canonical !== void 0) {
      tags.push(ownedTag("link", { rel: "canonical", href: canonical }));
      tags.push(ownedTag("meta", { property: "og:url", content: canonical }));
    }
    if (isPublic && siteName !== void 0) tags.push(ownedTag("meta", { property: "og:site_name", content: siteName }));
    if (isPublic) tags.push(ownedTag("meta", { property: "og:locale", content: locale }));
    if (isPublic && hreflangKey !== "") {
      for (const line of hreflangKey.split("\n")) {
        const space = line.indexOf(" ");
        tags.push(ownedTag("link", { rel: "alternate", hreflang: line.slice(0, space), href: line.slice(space + 1) }));
      }
    }
    for (const stale of Array.from(document.head.querySelectorAll(`[${OWNED}]`))) stale.remove();
    for (const tag of tags) document.head.appendChild(tag);
    return () => {
      for (const tag of tags) tag.remove();
    };
  }, [active, title, description, isPublic, canonical, siteName, locale, hreflangKey]);
}
function usePullToRefresh(onRefresh, options = {}) {
  const { threshold = 60, maxPull = 120 } = options;
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const startY = useRef(0);
  const scrollTopRef = useRef(0);
  const onTouchStart = useCallback((e) => {
    const container = e.currentTarget;
    scrollTopRef.current = container.scrollTop;
    if (scrollTopRef.current <= 0) {
      startY.current = e.touches[0].clientY;
      setIsPulling(true);
    }
  }, []);
  const onTouchMove = useCallback((e) => {
    if (!isPulling || isRefreshing) return;
    const container = e.currentTarget;
    if (container.scrollTop > 0) {
      setPullDistance(0);
      return;
    }
    const dy = e.touches[0].clientY - startY.current;
    if (dy > 0) {
      const distance = Math.min(dy * 0.5, maxPull);
      setPullDistance(distance);
    }
  }, [isPulling, isRefreshing, maxPull]);
  const onTouchEnd = useCallback(() => {
    if (!isPulling) return;
    setIsPulling(false);
    if (pullDistance >= threshold && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(threshold);
      onRefresh();
    } else {
      setPullDistance(0);
    }
  }, [isPulling, pullDistance, threshold, isRefreshing, onRefresh]);
  const endRefresh = useCallback(() => {
    setIsRefreshing(false);
    setPullDistance(0);
  }, []);
  const containerProps = {
    onTouchStart,
    onTouchMove,
    onTouchEnd,
    style: {
      transform: pullDistance > 0 ? `translateY(${pullDistance}px)` : void 0,
      transition: isPulling ? "none" : "transform 300ms ease-out"
    }
  };
  return {
    pullDistance,
    isPulling,
    isRefreshing,
    containerProps,
    endRefresh
  };
}
function useDragReorder(initialItems, onReorder) {
  const [items, setItems] = useState(initialItems);
  const [dragIndex, setDragIndex] = useState(-1);
  const [dragOverIndex, setDragOverIndex] = useState(-1);
  const itemsRef = useRef(initialItems);
  if (initialItems !== itemsRef.current) {
    itemsRef.current = initialItems;
    setItems(initialItems);
  }
  const isDragging = dragIndex >= 0;
  const handleDragStart = useCallback((index) => (e) => {
    e.preventDefault();
    setDragIndex(index);
    setDragOverIndex(index);
    e.target.setPointerCapture?.(e.pointerId);
  }, []);
  const handleDragMove = useCallback((index) => (e) => {
    if (dragIndex < 0) return;
    const target = document.elementFromPoint(e.clientX, e.clientY);
    if (!target) return;
    let el = target;
    while (el && !el.dataset.dragIndex) {
      el = el.parentElement;
    }
    if (el?.dataset.dragIndex) {
      const overIndex = parseInt(el.dataset.dragIndex, 10);
      if (!isNaN(overIndex) && overIndex !== dragOverIndex) {
        setDragOverIndex(overIndex);
      }
    }
  }, [dragIndex, dragOverIndex]);
  const handleDragEnd = useCallback(() => {
    if (dragIndex >= 0 && dragOverIndex >= 0 && dragIndex !== dragOverIndex) {
      const newItems = [...items];
      const [movedItem] = newItems.splice(dragIndex, 1);
      newItems.splice(dragOverIndex, 0, movedItem);
      setItems(newItems);
      onReorder(dragIndex, dragOverIndex, items[dragIndex]);
    }
    setDragIndex(-1);
    setDragOverIndex(-1);
  }, [dragIndex, dragOverIndex, items, onReorder]);
  const getDragHandleProps = useCallback((index) => ({
    onPointerDown: handleDragStart(index),
    style: { cursor: "grab", touchAction: "none" },
    "aria-grabbed": dragIndex === index,
    role: "button"
  }), [handleDragStart, dragIndex]);
  const getItemProps = useCallback((index) => ({
    onPointerMove: handleDragMove(index),
    onPointerUp: handleDragEnd,
    "aria-dropeffect": "move",
    "data-drag-index": String(index),
    style: {
      opacity: dragIndex === index ? 0.5 : 1,
      transition: isDragging ? "transform 150ms ease" : void 0,
      transform: isDragging && dragOverIndex >= 0 ? index === dragIndex ? "scale(1.02)" : index > dragIndex && index <= dragOverIndex ? "translateY(-100%)" : index < dragIndex && index >= dragOverIndex ? "translateY(100%)" : void 0 : void 0
    }
  }), [handleDragMove, handleDragEnd, dragIndex, dragOverIndex, isDragging]);
  return {
    items,
    dragIndex,
    dragOverIndex,
    isDragging,
    getDragHandleProps,
    getItemProps
  };
}

export { ALMADAR_DND_MIME, I18nProvider, PendingScopeContext, SharedEntityStoreContext, beginPending, claimDelivery, createSharedEntityStore, createTranslate, endPending, parseQueryBinding, runTickFrame, useAuthContext, useCanvasGestures, useDragReorder, useDraggable, useDropZone, useFormatContext, useInfiniteScroll, useLongPress, useMediaQuery, usePageHead, usePendingAction, usePendingRows, usePendingScopeValue, usePrefersReducedMotion, usePullToRefresh, useQuerySingleton, useRenderI18n, useScopeHasPending, useSharedEntitySnapshot, useSharedEntityStore, useSharedEntityStoreContext, useSwipeGesture, useTranslate };
